// Data access for the posts (blog) module panels. Pages stay thin; every
// /tenant/posts* call lives here.
import type { ApiClient } from "./api";

export interface PostCategory {
  id: string;
  slug: string;
  name: string;
  post_count?: number;
}

export interface PostListItem {
  id: string;
  slug: string;
  title: string;
  excerpt: string | null;
  author: string | null;
  published: number;
  published_at: string | null;
  updated_at: string;
  categories: PostCategory[];
}

export interface PostFaq {
  question: string;
  answer: string;
}

export interface PostDetail {
  id: string;
  slug: string;
  title: string;
  excerpt: string | null;
  /** Linear Editor.js document ({"blocks":[...]}) or "" for a new post. */
  content: string;
  featured_image_key: string | null;
  author_user_id: string | null;
  author: string | null;
  meta_title: string | null;
  meta_desc: string | null;
  faqs: string | null;
  published: number;
  published_at: string | null;
  updated_at: string;
  category_ids: string[];
}

export interface PostAuthor {
  user_id: string;
  name: string;
}

export interface PostSavePayload {
  title?: string;
  slug?: string;
  excerpt?: string | null;
  content?: string;
  featured_image_key?: string | null;
  author_user_id?: string | null;
  category_ids?: string[];
  meta_title?: string | null;
  meta_desc?: string | null;
  faqs?: PostFaq[];
  published?: 0 | 1;
  published_at?: string | null;
}

export async function fetchPosts(api: ApiClient): Promise<PostListItem[]> {
  const data = await api.request<{ posts: PostListItem[] }>("/tenant/posts");
  return data.posts ?? [];
}

export interface PostsPageResult {
  posts: PostListItem[];
  total: number;
}

/** Paged list for the Blog table; `query` is a list-controls query string. */
export async function fetchPostsPage(api: ApiClient, query: string): Promise<PostsPageResult> {
  const data = await api.request<{ posts?: PostListItem[]; total?: number }>(`/tenant/posts?${query}`);
  return { posts: data.posts ?? [], total: data.total ?? 0 };
}

export async function fetchPost(api: ApiClient, id: string): Promise<PostDetail> {
  const data = await api.request<{ post: PostDetail }>(`/tenant/posts/${id}`);
  return data.post;
}

export async function createPost(
  api: ApiClient,
  input: { title: string; slug: string }
): Promise<{ id: string; slug: string }> {
  return await api.request("/tenant/posts", {
    method: "POST",
    body: JSON.stringify(input),
  });
}

export async function savePost(
  api: ApiClient,
  id: string,
  payload: PostSavePayload
): Promise<{ id: string; slug?: string }> {
  return await api.request(`/tenant/posts/${id}`, {
    method: "PUT",
    body: JSON.stringify(payload),
  });
}

export async function deletePost(api: ApiClient, id: string): Promise<void> {
  await api.request(`/tenant/posts/${id}`, { method: "DELETE" });
}

export async function fetchPostAuthors(api: ApiClient): Promise<PostAuthor[]> {
  const data = await api.request<{ authors: PostAuthor[] }>("/tenant/posts/authors");
  return data.authors ?? [];
}

export async function fetchPostCategories(api: ApiClient): Promise<PostCategory[]> {
  const data = await api.request<{ categories: PostCategory[] }>("/tenant/post-categories");
  return data.categories ?? [];
}

export async function createPostCategory(
  api: ApiClient,
  name: string
): Promise<PostCategory> {
  const data = await api.request<{ category: PostCategory }>("/tenant/post-categories", {
    method: "POST",
    body: JSON.stringify({ name }),
  });
  return data.category;
}

export async function renamePostCategory(
  api: ApiClient,
  id: string,
  name: string
): Promise<void> {
  await api.request(`/tenant/post-categories/${id}`, {
    method: "PUT",
    body: JSON.stringify({ name }),
  });
}

export async function deletePostCategory(api: ApiClient, id: string): Promise<void> {
  await api.request(`/tenant/post-categories/${id}`, { method: "DELETE" });
}
